import React from "react";
import { DEMO_COLORS, DIFFICULTY_STYLES } from "./demoStyles";

/**
 * Biblioteca de cenários — modal com cards agrupados por dificuldade.
 * `onSelect(code)` dispara o auto-runner no backend.
 */
export default function ScenarioLibrary({
  open,
  onClose,
  scenarios,
  onSelect,
  disabled,
  loading,
}) {
  if (!open) return null;

  const grouped = { easy: [], medium: [], hard: [] };
  (scenarios || []).forEach((s) => {
    (grouped[s.difficulty] || grouped.medium).push(s);
  });

  return (
    <div
      data-testid="scenario-library-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(6px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="scenario-library-panel"
        style={{
          background: DEMO_COLORS.panel,
          borderRadius: 12,
          maxWidth: 1100,
          width: "100%",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: `1px solid ${DEMO_COLORS.divider}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: DEMO_COLORS.text,
              }}
            >
              🎬 Biblioteca de Cenários
            </div>
            <div style={{ fontSize: 12, color: DEMO_COLORS.textDim, marginTop: 3 }}>
              Escolha um cenário e o AgentUS conduz sozinho a conversa —
              você só acompanha.
            </div>
          </div>
          <button
            data-testid="scenario-library-close"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: DEMO_COLORS.textDim,
              fontSize: 22,
              cursor: "pointer",
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div
              style={{ color: DEMO_COLORS.textDim, textAlign: "center", padding: 40 }}
            >
              Carregando cenários...
            </div>
          ) : (
            Object.entries(grouped).map(([diff, items]) =>
              items.length === 0 ? null : (
                <div key={diff} style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: DIFFICULTY_STYLES[diff].color,
                      marginBottom: 10,
                      fontWeight: 600,
                    }}
                  >
                    {DIFFICULTY_STYLES[diff].label} — {items.length} cenário
                    {items.length > 1 ? "s" : ""}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {items.map((s) => (
                      <ScenarioCard
                        key={s.code}
                        scenario={s}
                        disabled={disabled}
                        onSelect={onSelect}
                      />
                    ))}
                  </div>
                </div>
              ),
            )
          )}
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ scenario, disabled, onSelect }) {
  const diff = DIFFICULTY_STYLES[scenario.difficulty] || DIFFICULTY_STYLES.medium;
  return (
    <div
      data-testid={`scenario-card-${scenario.code}`}
      style={{
        background: DEMO_COLORS.panelAlt,
        borderRadius: 10,
        padding: 14,
        border: `1px solid ${DEMO_COLORS.divider}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        transition: "border-color 160ms ease, transform 160ms ease",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = DEMO_COLORS.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = DEMO_COLORS.divider;
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 4,
            background: diff.bg,
            color: diff.color,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {diff.label}
        </div>
        <div style={{ fontSize: 10, color: DEMO_COLORS.textDim }}>
          {scenario.steps_count} passos
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: DEMO_COLORS.text, lineHeight: 1.3 }}>
        {scenario.title}
      </div>
      <div style={{ fontSize: 12, color: DEMO_COLORS.textDim, lineHeight: 1.5, minHeight: 32 }}>
        {scenario.description}
      </div>
      {scenario.commercial_goal && (
        <div
          style={{
            fontSize: 11,
            color: DEMO_COLORS.accentSoft,
            fontStyle: "italic",
            marginTop: 4,
          }}
        >
          🎯 {scenario.commercial_goal}
        </div>
      )}
      <button
        data-testid={`scenario-card-start-${scenario.code}`}
        disabled={disabled}
        onClick={() => onSelect(scenario.code)}
        style={{
          marginTop: 8,
          padding: "8px 12px",
          background: disabled ? DEMO_COLORS.divider : DEMO_COLORS.accent,
          color: "#000",
          border: "none",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        ▶ Iniciar Demonstração
      </button>
    </div>
  );
}
